import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import logo from '../Images/goheung-slogan.png';
import { region, locations } from '../data/region';
import GoheungMiniMap from '../components/GoheungMiniMap';

// 지점 목록과 지역 메타는 src/data/regions/<지역>/ 아래 한 벌뿐이다.
// krpano xml 생성 스크립트(scripts/)도 같은 파일을 읽으므로 메뉴와 xml 이 어긋나지 않는다.

const readKrpanoView = (krpano) => {
  const number = (name) => {
    const value = Number(krpano.get(name));
    return Number.isFinite(value) ? value : null;
  };

  const scene = String(krpano.get('xml.scene') || '').replace(/^scene_/, '');
  return {
    scene,
    hlookat: number('view.hlookat'),
    vlookat: number('view.vlookat'),
    fov: number('view.fov'),
  };
};

const VIEW_UPDATE_EPSILON = 0.05;
const VIEW_UPDATE_INTERVAL_MS = 50;

const sameViewNumber = (first, second) => {
  if (first === second) return true;
  return Number.isFinite(first) && Number.isFinite(second)
    && Math.abs(first - second) <= VIEW_UPDATE_EPSILON;
};

const sameViewState = (first, second) => {
  if (first === second) return true;
  if (!first || !second || first.scene !== second.scene) return false;
  return sameViewNumber(first.hlookat, second.hlookat)
    && sameViewNumber(first.vlookat, second.vlookat)
    && sameViewNumber(first.fov, second.fov);
};

const rounded = (value) => (Number.isFinite(value) ? Number(value.toFixed(3)) : null);

const startViewFromState = (view) => ({
  hlookat: rounded(view?.hlookat),
  vlookat: rounded(view?.vlookat),
  fov: rounded(view?.fov),
});

const PIN_PREVIEW_NAME = '__dev_pin_preview';
const KRPANO_EMBED_TIMEOUT_MS = 12000;
const KRPANO_AUTO_RETRY_LIMIT = 1;
const KRPANO_RUNTIME_PATH = '/krpano/tour.js';
const DESKTOP_MINIMAP_MEDIA = '(min-width: 701px)';

const clamp = (value, minimum, maximum) => Math.max(minimum, Math.min(maximum, value));

const SIDE_NAV_BASE_HEIGHT = 702;

// 작은 세로 화면에서는 로고 아래의 안전 영역부터 번호를 시작한다.
// 그 외에는 화면 중앙을 유지하되, 12개 항목 전체가 하단 밖으로 밀리지 않도록
// 번호·간격·글자를 동일한 비율로 축소한다.
const getSideNavigationLayout = (viewportWidth, viewportHeight) => {
  const left = clamp(Math.round(viewportWidth * 0.027), 16, 34);
  const compactViewport = viewportWidth <= 760 || viewportHeight <= 820;
  const logoClearance = compactViewport
    ? (viewportHeight <= 620 ? 94 : 142)
    // 로고(top 20px + 표시 높이 약 83px) 아래에 최소 28px을 남긴다.
    : 131;
  const bottomClearance = compactViewport ? 22 : 36;
  const availableHeight = Math.max(1, viewportHeight - logoClearance - bottomClearance);
  const scale = Math.min(1, Math.max(0.46, availableHeight / SIDE_NAV_BASE_HEIGHT));
  const renderedHeight = SIDE_NAV_BASE_HEIGHT * scale;
  const centeredTop = (viewportHeight - renderedHeight) / 2;
  const maximumTop = Math.max(logoClearance, viewportHeight - bottomClearance - renderedHeight);
  const top = Math.round(clamp(centeredTop, logoClearance, maximumTop));

  return { left, top, scale };
};

const SideNavigator = ({ locations: locationItems, activeLocationSlug, hoveredBox, isKrpanoLoading, onHover, onLeave, onSelect, reduceMotion, scale, left, top }) => (
  <SideNavigation aria-label="고흥 주요 지역" style={{ '--side-nav-scale': scale, '--side-nav-left': `${left}px`, '--side-nav-top': `${top}px` }}>
    {locationItems.map(({ no, slug, label }, index) => {
      const isActive = activeLocationSlug === slug;
      const isHovered = hoveredBox === slug;
      const isExpanded = isActive || isHovered;
      return (
        <SideNavigationItem
          key={slug}
          type="button"
          $active={isActive}
          $hovered={isHovered}
          aria-label={`${no}번 ${label}`}
          aria-current={isActive ? 'true' : undefined}
          aria-disabled={isKrpanoLoading}
          disabled={isKrpanoLoading}
          onClick={() => onSelect(slug)}
          onMouseEnter={() => onHover(slug)}
          onMouseLeave={onLeave}
          onFocus={() => onHover(slug)}
          onBlur={onLeave}
          initial={reduceMotion ? false : { opacity: 0, x: -14 }}
          animate={{ opacity: isKrpanoLoading ? 0.72 : 1, x: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.24, delay: reduceMotion ? 0 : 0.1 + index * 0.035 }}
        >
          <span className="side-navigation-number">{String(no).padStart(2, '0')}</span>
          <AnimatePresence initial={false}>
            {isExpanded && (
              <motion.span
                className="side-navigation-label"
                initial={reduceMotion ? false : { opacity: 0, x: -8, y: '-50%' }}
                animate={{ opacity: 1, x: 0, y: '-50%' }}
                exit={{ opacity: 0, x: -4, y: '-50%' }}
                transition={{ duration: reduceMotion ? 0 : 0.18, ease: [0.22, 1, 0.36, 1] }}
              >
                {label}
              </motion.span>
            )}
          </AnimatePresence>
        </SideNavigationItem>
      );
    })}
  </SideNavigation>
);

const KrpanoExample = () => {
  const containerRef = useRef(null);
  const krpanoRef = useRef(null);
  const [hoveredBox, setHoveredBox] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [doNotShowToday, setDoNotShowToday] = useState(false);
  const [viewState, setViewState] = useState(null);
  const [copyStatus, setCopyStatus] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [activeLocationSlug, setActiveLocationSlug] = useState(locations[0]?.slug || '');
  const [pinItems, setPinItems] = useState([]);
  const [selectedPinId, setSelectedPinId] = useState('');
  const [calloutDirection, setCalloutDirection] = useState('right');
  const [isPinPlacementMode, setIsPinPlacementMode] = useState(false);
  const [pendingPin, setPendingPin] = useState(null);
  const [pinStatus, setPinStatus] = useState('');
  const [isPinSaving, setIsPinSaving] = useState(false);
  const [isKrpanoLoading, setIsKrpanoLoading] = useState(false);
  const [krpanoLoadError, setKrpanoLoadError] = useState('');
  const [sideNavigationLayout, setSideNavigationLayout] = useState(() => getSideNavigationLayout(window.innerWidth, window.innerHeight));
  const [showMiniMap, setShowMiniMap] = useState(() => window.matchMedia(DESKTOP_MINIMAP_MEDIA).matches);
  const reduceMotion = useReducedMotion();
  const pinPlacementRef = useRef({ enabled: false, location: '', pin: null, calloutDirection: 'right' });
  const embeddingRef = useRef(false);
  const expectedLocationRef = useRef(locations[0]?.slug || '');
  const loadRequestRef = useRef(0);
  const viewerEventsCleanupRef = useRef(null);
  const loadTimeoutRef = useRef(null);
  const runtimeLoadPromiseRef = useRef(null);
  const pendingMapStartViewRef = useRef(null);
  const viewStateRef = useRef(null);
  const pendingViewKrpanoRef = useRef(null);
  const viewUpdateTimerRef = useRef(null);

  const clearKrpanoLoadTimeout = useCallback(() => {
    if (loadTimeoutRef.current !== null) {
      window.clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
  }, []);

  // 한국학 미팅 전 검수 화면에서는 개발용 핀·시작시점 도구를 숨긴다.
  // 다시 사용할 때만 true로 전환한다.
  const showViewTool = false;
  const canAutoSave = process.env.NODE_ENV !== 'production';
  const activeLocation = locations.find((location) => location.slug === activeLocationSlug) || locations[0];

  // krpano의 onviewchange는 화면을 움직이는 동안 매우 자주 호출될 수 있다.
  // React 상태는 최대 20fps로 합치고, 실제 값이 바뀐 경우에만 갱신해
  // 미니맵과 개발 패널의 불필요한 렌더링을 막는다.
  const commitViewState = useCallback((nextView) => {
    if (!nextView) {
      viewStateRef.current = null;
      setViewState(null);
      return;
    }

    setViewState((previous) => {
      if (sameViewState(previous, nextView)) {
        viewStateRef.current = previous;
        return previous;
      }
      viewStateRef.current = nextView;
      return nextView;
    });
  }, []);

  const readAndCommitView = useCallback((krpano) => {
    if (!krpano) return;
    try {
      commitViewState(readKrpanoView(krpano));
    } catch (error) {
      // krpano가 장면을 교체하는 순간에는 view 값이 잠시 준비되지 않을 수 있다.
    }
  }, [commitViewState]);

  const scheduleViewUpdate = useCallback((krpano) => {
    pendingViewKrpanoRef.current = krpano;
    if (viewUpdateTimerRef.current !== null) return;

    viewUpdateTimerRef.current = window.setTimeout(() => {
      viewUpdateTimerRef.current = null;
      const viewer = pendingViewKrpanoRef.current;
      pendingViewKrpanoRef.current = null;
      readAndCommitView(viewer);
    }, VIEW_UPDATE_INTERVAL_MS);
  }, [readAndCommitView]);

  const cancelScheduledViewUpdate = useCallback(() => {
    if (viewUpdateTimerRef.current !== null) {
      window.clearTimeout(viewUpdateTimerRef.current);
      viewUpdateTimerRef.current = null;
    }
    pendingViewKrpanoRef.current = null;
  }, []);

  useEffect(() => () => {
    cancelScheduledViewUpdate();
  }, [cancelScheduledViewUpdate]);

  // 좌측 번호 메뉴는 화면의 실제 안전 영역 안에서 균일 비율로만 축소한다.
  // 번호 간격과 글자 크기가 따로 변하거나 하단 카드와 겹치지 않도록 한다.
  useEffect(() => {
    let frameId = null;
    const updateSideNavigationLayout = () => {
      const nextLayout = getSideNavigationLayout(window.innerWidth, window.innerHeight);
      setSideNavigationLayout((previous) => (
        Math.abs(previous.scale - nextLayout.scale) < 0.002
        && previous.left === nextLayout.left
        && previous.top === nextLayout.top
          ? previous
          : nextLayout
      ));
    };
    const onResize = () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(updateSideNavigationLayout);
    };
    updateSideNavigationLayout();
    window.addEventListener('resize', onResize, { passive: true });
    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // 모바일에서는 파노라마와 지역 내비게이션의 터치 영역을 우선한다.
  // 컴포넌트 자체를 마운트하지 않아 지도 UI뿐 아니라 OSM 타일 요청도 발생하지 않는다.
  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_MINIMAP_MEDIA);
    const updateMiniMapVisibility = (event) => setShowMiniMap(event.matches);
    setShowMiniMap(mediaQuery.matches);
    mediaQuery.addEventListener('change', updateMiniMapVisibility);
    return () => mediaQuery.removeEventListener('change', updateMiniMapVisibility);
  }, []);

  const selectedPin = pinItems.find((pin) => pin.id === selectedPinId) || null;
  const currentPinPlacement = selectedPin?.placements?.find((placement) => placement.scene === viewState?.scene) || null;
  // 핀은 한 대지역에서 한 번만 배치한다. 현재 상세지역에 없더라도
  // 같은 대지역의 다른 상세지역에 저장값이 있으면 이미 배치된 콘텐츠다.
  const locationPinPlacement = selectedPin?.placements?.[0] || null;
  const hasPinPlacementInLocation = Boolean(selectedPin?.placements?.length);
  const savedCalloutDirection = (currentPinPlacement || locationPinPlacement)?.calloutDirection;

  // 이미 저장된 핀을 다시 고를 때는 현재 장면 또는 대지역의 기존 방향을 도구에도 그대로 보여준다.
  useEffect(() => {
    setCalloutDirection(savedCalloutDirection === 'left' ? 'left' : 'right');
  }, [selectedPinId, viewState?.scene, savedCalloutDirection]);

  useEffect(() => {
    pinPlacementRef.current = {
      enabled: isPinPlacementMode,
      location: activeLocationSlug,
      pin: selectedPin,
      calloutDirection,
    };
  }, [activeLocationSlug, calloutDirection, isPinPlacementMode, selectedPin]);

  const removePreviewHotspot = useCallback(() => {
    const krpano = krpanoRef.current;
    if (!krpano) return;
    try {
      const hotspot = krpano.get(`hotspot[${PIN_PREVIEW_NAME}]`);
      if (hotspot?.remove) hotspot.remove();
      else krpano.call(`removehotspot(${PIN_PREVIEW_NAME})`);
    } catch (error) {
      // 아직 viewer가 준비되지 않았거나 scene 전환 중인 경우는 무시한다.
    }
  }, []);

  const showPinPreview = useCallback((krpano, pin, placement) => {
    try {
      const old = krpano.get(`hotspot[${PIN_PREVIEW_NAME}]`);
      if (old?.remove) old.remove();
      else if (old) krpano.call(`removehotspot(${PIN_PREVIEW_NAME})`);

      const hotspot = krpano.addhotspot(PIN_PREVIEW_NAME);
      if (typeof hotspot.loadstyle === 'function') hotspot.loadstyle('callout');
      else hotspot.style = 'callout';
      hotspot.ath = placement.ath;
      hotspot.atv = placement.atv;
      hotspot.title = pin.title;
      hotspot.content = '저장 전 미리보기';
      hotspot.use3dtransition = true;
      if (/^(LOCAL_|HWP_NO_ID_)/.test(pin.id)) {
        hotspot.pin_type = 'temporary';
        hotspot.pin_color = region.theme.temporaryPin || '#A855F7';
        hotspot.color = region.theme.temporaryPinKrpano || '0xA855F7';
        hotspot.title_background = region.theme.temporaryPinKrpano || '0xA855F7';
      }
      hotspot.rotate = placement.calloutDirection === 'left' ? 300 : 45;
      if (placement.calloutDirection === 'left') {
        // 서천 callout 공식 예시와 동일한 왼쪽 방향 보정값.
        hotspot.callout_bg_border = '0,2,0,0';
        hotspot.text_padding = '10 0 0 0';
      }
      hotspot.onclick = '';
    } catch (error) {
      console.error('핀 미리보기를 만들지 못했습니다:', error);
      setPinStatus('핀 미리보기를 만들지 못했습니다. 페이지를 새로고침해 다시 시도하세요.');
    }
  }, []);

  const capturePinPlacement = useCallback((krpano) => {
    const selection = pinPlacementRef.current;
    if (!selection.enabled || !selection.pin) return;

    try {
      // 일반 DOM 좌표가 아니라 krpano가 관리하는 클릭 좌표를 공식 API에 넘긴다.
      const x = Number(krpano.get('mouse.clickx'));
      const y = Number(krpano.get('mouse.clicky'));
      const point = typeof krpano.screentosphere === 'function'
        ? krpano.screentosphere(x, y)
        : null;
      const ath = rounded(point?.x);
      const atv = rounded(point?.y);
      const scene = readKrpanoView(krpano).scene;
      if (!scene || ath === null || atv === null) {
        throw new Error('클릭 좌표를 파노라마 좌표로 변환하지 못했습니다.');
      }

      const placement = {
        location: selection.location,
        id: selection.pin.id,
        title: selection.pin.title,
        scene,
        ath,
        atv,
        calloutDirection: selection.calloutDirection === 'left' ? 'left' : 'right',
      };
      showPinPreview(krpano, selection.pin, placement);
      setPendingPin(placement);
      setIsPinPlacementMode(false);
      setPinStatus(`${selection.pin.title} 좌표를 자동 저장합니다…`);
    } catch (error) {
      setIsPinPlacementMode(false);
      setPinStatus(error.message || '핀 좌표를 읽지 못했습니다.');
    }
  }, [showPinPreview]);

  const resetLocationTools = useCallback(() => {
    cancelScheduledViewUpdate();
    commitViewState(null);
    setCopyStatus('');
    setSaveStatus('');
    setSelectedPinId('');
    setCalloutDirection('right');
    setPendingPin(null);
    setIsPinPlacementMode(false);
  }, [cancelScheduledViewUpdate, commitViewState]);

  const finishKrpanoLoad = useCallback((krpano, requestId) => {
    if (requestId !== loadRequestRef.current) return;
    try {
      let view = readKrpanoView(krpano);
      if (!view.scene || !view.scene.startsWith(`${expectedLocationRef.current}_`)) return;

      // 미니맵에서 이동한 경우에는 XML 장면 전환 뒤 이전 화면 각도가 남지 않도록
      // scenes.json에 사용자가 저장한 시작시점을 명시적으로 다시 적용한다.
      const pending = pendingMapStartViewRef.current;
      if (pending?.scene === view.scene) {
        const { hlookat, vlookat, fov } = pending;
        krpano.call(`lookat(${hlookat}, ${vlookat}, ${fov});`);
        pendingMapStartViewRef.current = null;
        view = readKrpanoView(krpano);
      }
      clearKrpanoLoadTimeout();
      setIsKrpanoLoading(false);
      setKrpanoLoadError('');
      commitViewState(view);
    } catch (error) {
      // 장면 교체 직후에는 view 값이 아직 준비되지 않을 수 있다.
    }
  }, [clearKrpanoLoadTimeout, commitViewState]);

  const failKrpanoLoad = useCallback((requestId, message = '파노라마를 불러오지 못했습니다.') => {
    if (requestId !== loadRequestRef.current) return;
    clearKrpanoLoadTimeout();
    embeddingRef.current = false;
    setIsKrpanoLoading(false);
    setKrpanoLoadError(message);
    console.error(`❌ KRPano 로드 실패: ${message}`);
  }, [clearKrpanoLoadTimeout]);

  const bindKrpanoEvents = useCallback((krpano) => {
    if (viewerEventsCleanupRef.current) viewerEventsCleanupRef.current();
    if (typeof krpano?.set !== 'function') {
      viewerEventsCleanupRef.current = null;
      return;
    }

    const handleLoadComplete = () => {
      // embedpano() 최초 로딩도 공식 onloadcomplete 이벤트로 처리한다.
      finishKrpanoLoad(krpano, loadRequestRef.current);
    };
    const handleLoadError = () => {
      failKrpanoLoad(loadRequestRef.current);
    };
    const handleViewChange = () => {
      scheduleViewUpdate(krpano);
    };

    // 이 프로젝트에 포함된 krpano 1.21 JS wrapper는 events 객체를 노출하지 않고,
    // 공식 events.* 속성에 JavaScript 함수를 직접 등록하는 인터페이스를 제공한다.
    krpano.set('events.onloadcomplete', handleLoadComplete);
    krpano.set('events.onloaderror', handleLoadError);
    krpano.set('events.onxmlerror', handleLoadError);
    krpano.set('events.onviewchange', handleViewChange);
    viewerEventsCleanupRef.current = () => {
      krpano.set('events.onloadcomplete', null);
      krpano.set('events.onloaderror', null);
      krpano.set('events.onxmlerror', null);
      krpano.set('events.onviewchange', null);
      if (pendingViewKrpanoRef.current === krpano) cancelScheduledViewUpdate();
    };
  }, [cancelScheduledViewUpdate, failKrpanoLoad, finishKrpanoLoad, scheduleViewUpdate]);

  const loadKrpanoRuntime = useCallback(() => {
    if (typeof window.embedpano === 'function') return Promise.resolve();
    if (runtimeLoadPromiseRef.current) return runtimeLoadPromiseRef.current;

    // index.html의 최초 tour.js 요청이 시간 초과·네트워크 오류로 실패한 경우에도
    // 전체 페이지 새로고침 없이 런타임만 다시 불러올 수 있게 한다.
    runtimeLoadPromiseRef.current = new Promise((resolve, reject) => {
      [...document.querySelectorAll('script[src]')]
        .filter((script) => {
          try {
            return new URL(script.src, window.location.href).pathname === KRPANO_RUNTIME_PATH;
          } catch (error) {
            return false;
          }
        })
        .forEach((script) => script.remove());

      const script = document.createElement('script');
      script.src = `${KRPANO_RUNTIME_PATH}?retry=${Date.now()}`;
      script.async = true;
      const timeout = window.setTimeout(() => {
        script.remove();
        reject(new Error('krpano 런타임 응답 시간이 초과되었습니다.'));
      }, KRPANO_EMBED_TIMEOUT_MS);
      script.onload = () => {
        window.clearTimeout(timeout);
        if (typeof window.embedpano === 'function') resolve();
        else reject(new Error('krpano 런타임을 초기화하지 못했습니다.'));
      };
      script.onerror = () => {
        window.clearTimeout(timeout);
        reject(new Error('krpano 런타임을 내려받지 못했습니다.'));
      };
      document.head.appendChild(script);
    }).finally(() => {
      runtimeLoadPromiseRef.current = null;
    });

    return runtimeLoadPromiseRef.current;
  }, []);

  // 서천 프로젝트와 같은 방식으로 지역마다 기존 viewer를 비우고 새로 embed한다.
  // 지역 XML·skin·scene 목록을 한 번에 초기화하므로 이전 지역의 썸네일이나 scene이 남지 않는다.
  const loadKrpano = useCallback(async (xmlFileName, retryCount = 0, initialScene = '') => {
    if (!containerRef.current) return;

    // 로드 중인 embed가 끝나기 전에는 새 embed를 겹쳐 실행하지 않는다.
    if (embeddingRef.current) return;

    const requestId = loadRequestRef.current + 1;
    loadRequestRef.current = requestId;
    clearKrpanoLoadTimeout();
    expectedLocationRef.current = xmlFileName;
    removePreviewHotspot();
    setActiveLocationSlug(xmlFileName);
    setIsKrpanoLoading(true);
    setKrpanoLoadError('');
    resetLocationTools();

    if (typeof window.embedpano !== 'function') {
      try {
        await loadKrpanoRuntime();
      } catch (error) {
        failKrpanoLoad(requestId, '파노라마 뷰어를 준비하지 못했습니다. 다시 시도해 주세요.');
        return;
      }
      // 런타임을 기다리는 사이 다른 지역을 선택했다면 오래된 요청은 버린다.
      if (requestId !== loadRequestRef.current) return;
    }

    viewerEventsCleanupRef.current?.();
    viewerEventsCleanupRef.current = null;
    try {
      // unload()은 서천 방식의 container 초기화에 더해 기존 WebGL 리소스도 정리한다.
      if (krpanoRef.current?.unload) krpanoRef.current.unload();
    } catch (error) {
      console.warn('기존 KRPano 뷰어 정리 중 경고:', error);
    }
    krpanoRef.current = null;
    containerRef.current.innerHTML = '';
    embeddingRef.current = true;

    // XML 요청 자체가 늦거나 끊기면 onready()조차 호출되지 않아 검은 화면에 남는다.
    // 한 번은 자동 재시도하고, 다시 실패한 경우에는 사용자가 즉시 복구할 수 있게 한다.
    loadTimeoutRef.current = window.setTimeout(() => {
      if (requestId !== loadRequestRef.current) return;

      embeddingRef.current = false;
      try {
        krpanoRef.current?.unload?.();
      } catch (error) {
        console.warn('시간 초과된 KRPano 뷰어 정리 중 경고:', error);
      }
      krpanoRef.current = null;
      if (containerRef.current) containerRef.current.innerHTML = '';

      if (retryCount < KRPANO_AUTO_RETRY_LIMIT) {
        loadKrpano(xmlFileName, retryCount + 1, initialScene);
        return;
      }
      failKrpanoLoad(requestId, '파노라마 서버의 응답이 지연되고 있습니다. 다시 시도해 주세요.');
    }, KRPANO_EMBED_TIMEOUT_MS);

    try {
      window.embedpano({
        swf: null,
        xml: `/krpano/${xmlFileName}.xml`,
        // vtourskin의 공식 startscene 변수를 이용해, 다른 지역을 다시 embed해도
        // 기본 첫 장면을 거치지 않고 목적지 장면에서 시작한다.
        vars: initialScene ? { startscene: `scene_${initialScene}` } : {},
        target: containerRef.current,
        html5: 'only',
        width: '100%',
        height: '100%',
        onready: (readyKrpano) => {
          if (requestId !== loadRequestRef.current) {
            readyKrpano?.unload?.();
            return;
          }
          embeddingRef.current = false;
          krpanoRef.current = readyKrpano;
          bindKrpanoEvents(readyKrpano);
          // krpano 1.21 공식 JavaScript API: mouse.clickx/y → screentosphere() → ath/atv.
          // 이벤트를 krpano 안에 등록하므로 브라우저 배율·iframe 위치에 따른 오차가 없다.
          if (showViewTool) {
            readyKrpano.set('events.onclick', () => capturePinPlacement(readyKrpano));
          }
          // 운영 화면의 미니맵도 현재 scene/시야를 따라가야 하므로
          // 개발용 도구 표시 여부와 관계없이 view 상태를 갱신한다.
          readAndCommitView(readyKrpano);
        },
      });
    } catch (error) {
      embeddingRef.current = false;
      failKrpanoLoad(requestId, error.message);
    }
  }, [bindKrpanoEvents, capturePinPlacement, clearKrpanoLoadTimeout, failKrpanoLoad, loadKrpanoRuntime, readAndCommitView, removePreviewHotspot, resetLocationTools, showViewTool]);

  // XML 핫스팟의 js(goheungNavigateScene(...))가 호출하는 단일 이동 관문.
  // URL이나 객체를 넘기지 않고, locations.json에 있는 slug/scene 문자열만 허용한다.
  useEffect(() => {
    const navigateToScene = (targetLocationSlug, targetSceneId, options = null) => {
      const targetLocation = locations.find((location) => location.slug === targetLocationSlug);
      // React은 핀 저장 때 전체 화면이 다시 빌드되지 않도록 menu.json(지역명·slug만)을
      // 읽는다. 따라서 scene 배열은 여기에는 존재하지 않는다. 대상 scene의 실존 여부는
      // XML 생성 단계에서 locations.json 전체를 기준으로 이미 검증한다.
      // 런타임에서는 메뉴에 등록된 지역과 안전한 scene ID 형식만 한 번 더 확인한다.
      const isSafeSceneId = typeof targetSceneId === 'string'
        && /^[a-z0-9_]+$/i.test(targetSceneId)
        && targetSceneId.startsWith(`${targetLocationSlug}_`);
      if (!targetLocation || !isSafeSceneId) {
        console.error('다른 시점 대상이 올바르지 않습니다:', targetLocationSlug, targetSceneId);
        setKrpanoLoadError('다른 시점 정보를 찾지 못했습니다.');
        return;
      }


      const startView = options?.startView;
      const hlookat = Number(startView?.hlookat);
      const vlookat = Number(startView?.vlookat);
      const fov = Number(startView?.fov);
      pendingMapStartViewRef.current = [hlookat, vlookat, fov].every(Number.isFinite)
        ? { scene: targetSceneId, hlookat, vlookat, fov }
        : null;

      // 같은 지역 안 장면은 viewer를 다시 만들지 않고 krpano의 공식 loadscene()으로 전환한다.
      if (expectedLocationRef.current === targetLocationSlug && krpanoRef.current) {
        try {
          krpanoRef.current.call(`loadscene(scene_${targetSceneId}, null, MERGE, BLEND(0.45));`);
          setActiveLocationSlug(targetLocationSlug);
          return;
        } catch (error) {
          console.error('같은 지역의 다른 시점 전환 실패:', error);
        }
      }

      // 다른 지역도 React에서 viewer를 unload()/embed()하지 않는다. callout 클릭 중
      // viewer DOM을 제거하면 플러그인의 남은 render loop가 null.style을 읽을 수 있다.
      // krpano 공식 loadpanoscene()가 대상 XML 안의 지정 scene을 바로 전환한다.
      // loadpano()+startscene은 skin의 onstart 동작에 의존해, 새 XML은 열렸지만
      // 장면 이미지가 비어 있는 상태가 될 수 있다.
      const krpano = krpanoRef.current;
      if (krpano) {
        const requestId = loadRequestRef.current + 1;
        loadRequestRef.current = requestId;
        expectedLocationRef.current = targetLocationSlug;
        setActiveLocationSlug(targetLocationSlug);
        setKrpanoLoadError('');
        try {
          krpano.call(
            `loadpanoscene('/krpano/${targetLocationSlug}.xml', scene_${targetSceneId}, null, MERGE, BLEND(0.45), js(goheungPanoLoadDone()));`
          );
          return;
        } catch (error) {
          console.error('krpano 내부 다른 시점 전환 실패:', error);
        }
      }

      // 뷰어가 아직 준비되기 전인 예외 상황만 기존 embed 경로로 복구한다.
      loadKrpano(targetLocationSlug, 0, targetSceneId);
    };

    window.goheungNavigateScene = navigateToScene;
    window.goheungPanoLoadDone = () => {
      const krpano = krpanoRef.current;
      if (!krpano) return;
      // loadpanoscene()은 기존 XML의 events도 교체하므로 새 XML에 이벤트를 다시 연결한다.
      bindKrpanoEvents(krpano);
      // 지역 간 전환은 skin을 유지하므로 대상 지역의 scene 목록으로 카드 레이어를
      // 명시적으로 다시 만든다. 이전 지역의 썸네일이 남는 것을 막는다.
      krpano.call('goheung_rebuild_scene_thumbs();');
      if (showViewTool) {
        krpano.set('events.onclick', () => capturePinPlacement(krpano));
      }
      finishKrpanoLoad(krpano, loadRequestRef.current);
      commitViewState(readKrpanoView(krpano));
    };
    return () => {
      if (window.goheungNavigateScene === navigateToScene) {
        delete window.goheungNavigateScene;
      }
      delete window.goheungPanoLoadDone;
    };
  }, [bindKrpanoEvents, capturePinPlacement, commitViewState, finishKrpanoLoad, loadKrpano, showViewTool]);

  // React 화면이 제거될 때만 공식 unload()로 뷰어와 WebGL 리소스를 정리한다.
  useEffect(() => () => {
    clearKrpanoLoadTimeout();
    viewerEventsCleanupRef.current?.();
    viewerEventsCleanupRef.current = null;
    if (krpanoRef.current?.unload) krpanoRef.current.unload();
    krpanoRef.current = null;
  }, [clearKrpanoLoadTimeout]);

  // 개발 화면에는 선택한 지역의 '확정 핀(pins)'만 전달한다. catalog.json 전체는 번들에 넣지 않는다.
  useEffect(() => {
    if (!showViewTool || !activeLocationSlug) return undefined;
    const controller = new AbortController();

    const loadPinItems = async () => {
      try {
        const response = await fetch(`/__dev/pin-placement-data?location=${encodeURIComponent(activeLocationSlug)}`, {
          signal: controller.signal,
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.error || '핀 목록을 불러오지 못했습니다.');
        setPinItems(result.pins || []);
      } catch (error) {
        if (error.name !== 'AbortError') {
          setPinItems([]);
          setPinStatus(error instanceof TypeError
            ? '핀 목록 API에 연결할 수 없습니다. npm start 개발 서버에서 실행하세요.'
            : (error.message || '핀 목록을 불러오지 못했습니다.'));
        }
      }
    };

    loadPinItems();
    return () => controller.abort();
  }, [activeLocationSlug, showViewTool]);

  // XML의 onloadcomplete 이벤트가 skin include에 의해 교체되는 경우에도
  // 장면이 실제로 생성됐는지 확인해 개발/운영 로딩 오버레이가 남지 않게 한다.
  useEffect(() => {
    if (!isKrpanoLoading) return undefined;
    const timer = window.setInterval(() => {
      const krpano = krpanoRef.current;
      if (!krpano) return;
      try {
        const scene = readKrpanoView(krpano).scene;
        if (scene && scene.startsWith(`${expectedLocationRef.current}_`)) {
          finishKrpanoLoad(krpano, loadRequestRef.current);
        }
      } catch (error) {
        // 장면 교체 중에는 잠시 get()이 실패할 수 있다.
      }
    }, 200);
    return () => window.clearInterval(timer);
  }, [finishKrpanoLoad, isKrpanoLoading]);

  const copyView = async () => {
    if (!viewState?.scene) return;

    const payload = {
      id: viewState.scene,
      startView: startViewFromState(viewState),
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopyStatus('복사됨');
    } catch (error) {
      setCopyStatus('복사 실패');
    }
    window.setTimeout(() => setCopyStatus(''), 1500);
  };

  const saveStartView = async () => {
    if (!viewState?.scene) return;

    const startView = startViewFromState(viewState);
    if (Object.values(startView).some((value) => value === null)) {
      setSaveStatus('현재 view 값이 아직 준비되지 않았습니다.');
      return;
    }

    setIsSaving(true);
    setSaveStatus('저장 중…');
    try {
      const response = await fetch('/__dev/save-start-view', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: viewState.scene, startView }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || '자동 저장 API에 연결할 수 없습니다.');
      setSaveStatus(`${result.id || viewState.scene} 저장됨`);
    } catch (error) {
      const message = error instanceof TypeError
        ? '자동 저장 API에 연결할 수 없습니다. npm start 개발 서버에서 실행하세요.'
        : (error.message || '저장 실패');
      setSaveStatus(message);
    } finally {
      setIsSaving(false);
    }
  };

  const startPinPlacement = () => {
    if (!selectedPin || !viewState?.scene) return;
    removePreviewHotspot();
    setPendingPin(null);
    setIsPinPlacementMode(true);
    const direction = calloutDirection === 'left' ? '왼쪽' : '오른쪽';
    setPinStatus(`「${selectedPin.title}」을(를) ${direction} 방향으로 배치합니다. 파노라마의 실제 위치를 한 번 클릭하세요.`);
  };

  const savePinPlacement = useCallback(async () => {
    if (!pendingPin) return;

    setIsPinSaving(true);
    setPinStatus('좌표와 XML을 저장 중…');
    try {
      const response = await fetch('/__dev/save-pin-placement', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pendingPin),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || '핀 좌표 저장에 실패했습니다.');

      setPinItems((items) => items.map((pin) => {
        if (pin.id !== pendingPin.id) return pin;
        // 한 대지역 안에서는 콘텐츠당 위치 하나만 유지한다. 다른 상세지역에서
        // 다시 찍으면 기존 위치를 새 위치로 옮겨 중복 콜아웃을 만들지 않는다.
        return { ...pin, placements: [result.placement] };
      }));

      // 저장 전 미리보기를 서천과 같은 실제 callout 동작으로 바꾼다. XML도 서버에서
      // 동시에 갱신했으므로, 다음 지역/장면 재로딩 후에도 동일하게 유지된다.
      const preview = krpanoRef.current?.get(`hotspot[${PIN_PREVIEW_NAME}]`);
      if (preview) {
        if (region.tocBaseUrl) {
          preview.content = '더 알아보기 〉';
          preview.onclick = `popup('iframe', '${region.tocBaseUrl}${pendingPin.id}', ${region.popupSize.width}, ${region.popupSize.height}, true);`;
        } else {
          preview.content = '';
          preview.onclick = '';
        }
      }
      setPendingPin(null);
      setPinStatus(result.warning || `${pendingPin.title} 저장됨 — ${pendingPin.scene} XML까지 갱신했습니다.`);
    } catch (error) {
      setPinStatus(error instanceof TypeError
        ? '자동 저장 API에 연결할 수 없습니다. npm start 개발 서버에서 실행하세요.'
        : (error.message || '핀 좌표 저장에 실패했습니다.'));
    } finally {
      setIsPinSaving(false);
    }
  }, [pendingPin]);

  // 콘텐츠를 고르고 '위치 찍기'를 누른 뒤 파노라마를 클릭하면 바로 저장한다.
  // 좌표 확인 후 다시 저장 버튼을 누르던 단계를 없애되, 실제 클릭 전에는 저장하지 않는다.
  useEffect(() => {
    if (!pendingPin || isPinSaving) return;
    savePinPlacement();
  }, [isPinSaving, pendingPin, savePinPlacement]);

  const removeCurrentPinPlacement = async () => {
    if (!selectedPin || !viewState?.scene) return;
    const scene = viewState.scene;
    setIsPinSaving(true);
    setPinStatus('현재 장면의 핀 좌표를 삭제 중…');
    try {
      const response = await fetch('/__dev/remove-pin-placement', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ location: activeLocationSlug, id: selectedPin.id, scene }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || '핀 좌표 삭제에 실패했습니다.');
      setPinItems((items) => items.map((pin) => (
        pin.id === selectedPin.id
          ? { ...pin, placements: (pin.placements || []).filter((item) => item.scene !== scene) }
          : pin
      )));
      setPendingPin(null);
      removePreviewHotspot();
      setPinStatus(result.warning || `${selectedPin.title}의 ${scene} 핀을 삭제했습니다. 화면을 다시 불러옵니다.`);
      loadKrpano(activeLocationSlug);
    } catch (error) {
      setPinStatus(error instanceof TypeError
        ? '자동 저장 API에 연결할 수 없습니다. npm start 개발 서버에서 실행하세요.'
        : (error.message || '핀 좌표 삭제에 실패했습니다.'));
    } finally {
      setIsPinSaving(false);
    }
  };

  useEffect(() => {
    const popupStatus = localStorage.getItem('hidePopupUntil');
    const now = new Date();

    if (!popupStatus || new Date(popupStatus) < now) {
      setShowPopup(true);
    }

    // 촬영 데이터가 준비된 뒤에는 실제 첫 지역을 초기 장면으로 연다.
    // 이후 좌측 메뉴에서 1~12번 지역과 각 지역의 드롭다운 scene을 선택한다.
    loadKrpano(locations[0].slug);
  }, [loadKrpano]);

  //팝업창 닫는 이벤트
  const handleClosePopup = () => {
    if (doNotShowToday) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      localStorage.setItem('hidePopupUntil', tomorrow.toISOString());
    }
    setShowPopup(false);
  };

  // 미니맵의 촬영 위치를 눌렀을 때도 핀의 다른 시점 이동과 같은
  // krpano 공식 전환 관문을 사용한다. 전역 관문이 아직 등록되기 전의
  // 초기 렌더링 순간에는 기존 embed 경로를 안전하게 사용한다.
  const navigateFromMap = useCallback((locationSlug, sceneId, startView) => {
    if (typeof window.goheungNavigateScene === 'function') {
      window.goheungNavigateScene(locationSlug, sceneId, { startView });
      return;
    }
    const hlookat = Number(startView?.hlookat);
    const vlookat = Number(startView?.vlookat);
    const fov = Number(startView?.fov);
    pendingMapStartViewRef.current = [hlookat, vlookat, fov].every(Number.isFinite)
      ? { scene: sceneId, hlookat, vlookat, fov }
      : null;
    loadKrpano(locationSlug, 0, sceneId);
  }, [loadKrpano]);

  return (
    <>
      <Logo
        src={logo}
        alt={region.logoAlt}
        $clickable={Boolean(region.logoLink)}
        onClick={region.logoLink ? () => window.open(region.logoLink, '_blank', 'noopener') : undefined}
      />
      {activeLocation && (
        <LocationContext aria-label={`현재 지역 ${activeLocation.no}번 ${activeLocation.label}`}>
          <LocationContextEyebrow>GOHEUNG PANORAMA</LocationContextEyebrow>
          <LocationContextTitle>
            <strong>{String(activeLocation.no).padStart(2, '0')}</strong>
            <span>{activeLocation.label}</span>
          </LocationContextTitle>
        </LocationContext>
      )}

      <SideNavigator
        locations={locations}
        activeLocationSlug={activeLocationSlug}
        hoveredBox={hoveredBox}
        isKrpanoLoading={isKrpanoLoading}
        onHover={setHoveredBox}
        onLeave={() => setHoveredBox(null)}
        onSelect={loadKrpano}
        reduceMotion={reduceMotion}
        scale={sideNavigationLayout.scale}
        left={sideNavigationLayout.left}
        top={sideNavigationLayout.top}
      />

      <div ref={containerRef} style={{ width: '100%', height: '100vh', position: 'relative' }} />
      {showMiniMap && (
        <GoheungMiniMap
          activeLocationSlug={activeLocationSlug}
          activeSceneId={viewState?.scene || ''}
          onNavigate={navigateFromMap}
        />
      )}
      {isKrpanoLoading && <TransitionShade aria-hidden="true" />}
      {krpanoLoadError && (
        <ViewerStatusOverlay role="alert" aria-live="assertive" $error>
          <ViewerStatusCard>
            <span>{krpanoLoadError}</span>
            <ViewerRetryButton type="button" onClick={() => loadKrpano(activeLocationSlug)}>
              다시 시도
            </ViewerRetryButton>
          </ViewerStatusCard>
        </ViewerStatusOverlay>
      )}
      {showViewTool && (
        <ViewToolPanel $theme={region.theme}>
          <ViewToolTitle>개발용 핀 배치</ViewToolTitle>
          <ViewToolScene>현재 장면: {viewState?.scene || 'krpano 준비 중'}</ViewToolScene>
          <AdvancedToolDetails>
            <summary>핀 배치 도구 열기</summary>
            <ViewToolHint>
              1. 콘텐츠 선택<br />
              2. 말풍선 방향 선택 후 ‘위치 찍기’ 누르기<br />
              3. 파노라마의 실제 대상 클릭 — 즉시 자동 저장<br />
              <small>이 지역에서 한 번 표시된 콘텐츠는 모든 상세지역에서 취소선으로 표시됩니다. 취소선 항목도 다시 지정할 수 있습니다.</small>
            </ViewToolHint>
            <PinSelect
              value={selectedPinId}
              onChange={(event) => {
                removePreviewHotspot();
                const nextPinId = event.target.value;
                const nextPin = pinItems.find((pin) => pin.id === nextPinId);
                const alreadyPlaced = Boolean(nextPin?.placements?.length);
                setSelectedPinId(nextPinId);
                setPendingPin(null);
                setIsPinPlacementMode(false);
                setPinStatus(alreadyPlaced
                  ? '이 지역에 이미 표시된 콘텐츠입니다. 위치 다시 찍기로 다른 상세지역으로 옮길 수 있습니다.'
                  : '');
              }}
              disabled={!canAutoSave || pinItems.length === 0}
            >
              <option value="">관련 콘텐츠 선택 ({pinItems.length}건)</option>
              {pinItems.map((pin) => {
                const isPlacedInLocation = Boolean(pin.placements?.length);
                const optionLabel = `${pin.title} · ${pin.id}`;
                return (
                  <option
                    key={pin.id}
                    value={pin.id}
                    className={isPlacedInLocation ? 'pin-option-placed' : undefined}
                    style={isPlacedInLocation
                      ? { textDecoration: 'line-through', textDecorationThickness: '3px', fontWeight: 700 }
                      : undefined}
                    title={isPlacedInLocation ? '이 지역에 이미 표시됨 — 다시 위치 지정 가능' : undefined}
                  >
                    {isPlacedInLocation ? '●' : '○'} {isPlacedInLocation ? strikeText(optionLabel) : optionLabel}
                  </option>
                );
              })}
            </PinSelect>
            <CalloutDirectionControl disabled={!selectedPin || isPinSaving}>
              <legend>말풍선 방향</legend>
              <label>
                <input
                  type="radio"
                  name="callout-direction"
                  value="right"
                  checked={calloutDirection === 'right'}
                  onChange={() => setCalloutDirection('right')}
                />
                오른쪽 (기본)
              </label>
              <label>
                <input
                  type="radio"
                  name="callout-direction"
                  value="left"
                  checked={calloutDirection === 'left'}
                  onChange={() => setCalloutDirection('left')}
                />
                왼쪽
              </label>
            </CalloutDirectionControl>
            {selectedPin && (
              <PinPlacementInfo>
                {currentPinPlacement
                  ? `현재 장면 저장값: ath ${formatViewValue(currentPinPlacement.ath)}, atv ${formatViewValue(currentPinPlacement.atv)} · 다시 찍어 덮어쓰기 가능`
                  : hasPinPlacementInLocation
                    ? '이 지역의 다른 상세지역에 이미 표시됨 · 위치 다시 찍기로 이 장면으로 이동 가능'
                    : '이 지역에는 아직 저장된 위치가 없습니다.'}
              </PinPlacementInfo>
            )}
            <ViewToolButton
              type="button"
              onClick={startPinPlacement}
              $theme={region.theme}
              disabled={!canAutoSave || !selectedPin || !viewState?.scene || isPinSaving}
            >
              {isPinPlacementMode
                ? '지금 파노라마를 클릭하세요'
                : currentPinPlacement || hasPinPlacementInLocation
                  ? '위치 다시 찍기 → 화면 클릭'
                  : '위치 찍기 → 화면 클릭'}
            </ViewToolButton>
            {pendingPin && (
              <PinPlacementInfo>
                자동 저장 중: ath {formatViewValue(pendingPin.ath)}, atv {formatViewValue(pendingPin.atv)}
              </PinPlacementInfo>
            )}
            {currentPinPlacement && !pendingPin && (
              <DangerToolButton type="button" onClick={removeCurrentPinPlacement} disabled={isPinSaving}>
                현재 장면 위치 삭제
              </DangerToolButton>
            )}
            {pinStatus && <ViewToolStatus>{pinStatus}</ViewToolStatus>}
          </AdvancedToolDetails>

          <ToolDivider />
          <AdvancedToolDetails>
            <summary>시작시점 도구 열기</summary>
            <ViewToolValues>
              <span>hlookat</span><strong>{formatViewValue(viewState?.hlookat)}</strong>
              <span>vlookat</span><strong>{formatViewValue(viewState?.vlookat)}</strong>
              <span>fov</span><strong>{formatViewValue(viewState?.fov)}</strong>
            </ViewToolValues>
            <ViewToolButton
              type="button"
              onClick={saveStartView}
              $theme={region.theme}
              disabled={!canAutoSave || isSaving || !viewState?.scene}
            >
              {isSaving ? '저장 중…' : '현재 장면 시작시점 저장'}
            </ViewToolButton>
            <ViewToolButton type="button" onClick={copyView} $theme={region.theme} disabled={!viewState?.scene}>
              {copyStatus || '장면 값 복사(수동용)'}
            </ViewToolButton>
            {saveStatus && <ViewToolStatus>{saveStatus}</ViewToolStatus>}
          </AdvancedToolDetails>
        </ViewToolPanel>
      )}
      {showPopup && (
        <PopupOverlay role="presentation">
          <PopupBox role="dialog" aria-modal="true" aria-labelledby="welcome-title">
            <PopupEyebrow>GOHEUNG PANORAMA</PopupEyebrow>
            <PopupHeading id="welcome-title">{region.popupTitle}</PopupHeading>
            <PopupText>{region.popupBody}</PopupText>
            <PopupGuideList>
              <li><GuideNumber $theme={region.theme}>1</GuideNumber>왼쪽 지역 메뉴에서 주요 명소를 선택하세요.</li>
              <li><GuideNumber $theme={region.theme}>2</GuideNumber>아래 사진을 눌러 세부 촬영지로 이동하세요.</li>
              <li><GuideNumber $theme={region.theme}>3</GuideNumber>화면의 핀에서 관련 콘텐츠를 확인하세요.</li>
            </PopupGuideList>
            <PopupOptions>
              <label>
                <input
                  type="checkbox"
                  checked={doNotShowToday}
                  onChange={(e) => setDoNotShowToday(e.target.checked)}
                />
                오늘 하루 보지 않기
              </label>
              <CloseButton onClick={handleClosePopup} $theme={region.theme}>닫기</CloseButton>
            </PopupOptions>
          </PopupBox>
        </PopupOverlay>
      )}
    </>
  );
};

export default KrpanoExample;

const formatViewValue = (value) => (Number.isFinite(value) ? value.toFixed(3) : '—');

// macOS/Chrome의 네이티브 select는 option의 text-decoration을 무시할 수 있다.
// 취소선을 문자열 자체에도 넣어 기존에 이미 배치된 항목이 항상 보이게 한다.
const strikeText = (value) => [...String(value)]
  .map((character) => /\s/.test(character) ? character : `${character}\u0336`)
  .join('');

// 스타일 컴포넌트
// 고흥군 군정 슬로건(두 줄형). 가로세로비가 2:1 이라 한 줄짜리 로고보다 넓게 잡아야 읽힌다.
// 항공사진 위에 얹히는데 슬로건의 "더큰"·"행복한" 이 짙은 회색이라, 어두운 화면에서는
// 묻힐 수 있어 옅은 드롭섀도로 윤곽만 살렸다. 원본 아트워크 자체는 손대지 않았다.
const Logo = styled.img`
  position: absolute;
  top: 20px;
  left: 20px;
  width: 170px;
  height: auto;
  z-index: 1000;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  filter: drop-shadow(0 0 3px rgba(255, 255, 255, 0.85));

  @media (max-width: 760px) and (max-height: 620px) {
    top: 12px;
    left: 16px;
    width: 126px;
  }
`;

// 드롭다운을 대체하지 않는 현재 위치 표기다. 장면을 조작하지 않고,
// 넓은 파노라마 화면에서 사용자가 어느 큰 지역을 보고 있는지만 알려 준다.
const LocationContext = styled.div`
  position: absolute;
  top: 24px;
  right: 28px;
  z-index: 1000;
  min-width: 142px;
  padding: 0 0 8px;
  color: #ffffff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.76);
  text-align: right;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.72);
  pointer-events: none;

  @media (max-width: 700px) {
    top: 18px;
    right: 16px;
    min-width: 112px;
    padding-bottom: 6px;
  }
`;

const LocationContextEyebrow = styled.div`
  margin-bottom: 3px;
  color: rgba(255, 255, 255, 0.78);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.16em;

  @media (max-width: 700px) {
    font-size: 8px;
  }
`;

const LocationContextTitle = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.03em;
  white-space: nowrap;

  strong {
    color: #f04f25;
    font-size: 18px;
    letter-spacing: 0.05em;
  }

  @media (max-width: 700px) {
    gap: 5px;
    font-size: 12px;

    strong {
      font-size: 15px;
    }
  }
`;

const SideNavigation = styled.nav`
  position: absolute;
  top: var(--side-nav-top, 106px);
  left: var(--side-nav-left, 34px);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 330px;
  transform: scale(var(--side-nav-scale, 1));
  transform-origin: left top;

  @media (prefers-reduced-motion: reduce) {
    &, & * {
      transition-duration: 0.01ms !important;
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
    }
  }
`;

const SideNavigationItem = styled(motion.button)`
  position: relative;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  padding: 0;
  border: 1.5px solid rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  background: ${({ $active, $hovered }) => ($active ? '#f04f25' : ($hovered ? 'rgba(41, 54, 131, 0.96)' : 'rgba(16, 46, 69, 0.84)'))};
  box-shadow: ${({ $active }) => ($active ? '0 0 0 7px rgba(240, 79, 37, 0.19), 0 5px 16px rgba(6, 16, 24, 0.26)' : '0 3px 10px rgba(6, 16, 24, 0.18)')};
  color: #ffffff;
  cursor: pointer;
  outline: none;
  transition: background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;

  &:disabled {
    cursor: wait;
  }

  &:focus-visible {
    border-color: #f04f25;
    box-shadow: 0 0 0 4px rgba(240, 79, 37, 0.34);
  }

  .side-navigation-number {
    display: block;
    font-family: Arial, sans-serif;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.04em;
    line-height: 40px;
    text-align: center;
  }

  .side-navigation-label {
    position: absolute;
    top: 50%;
    left: 54px;
    min-width: max-content;
    transform: translateY(-50%);
    color: #ffffff;
    font-family: Arial, sans-serif;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: -0.04em;
    line-height: 1.15;
    pointer-events: none;
    text-align: left;
    text-shadow: 0 1px 3px rgba(5, 20, 30, 0.64);
  }
`;

const TransitionShade = styled.div`
  position: fixed;
  inset: 0;
  z-index: 850;
  pointer-events: none;
  background: rgba(0, 0, 0, 0.14);
  animation: transition-shade 0.3s ease-out;

  @keyframes transition-shade {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ViewerStatusOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 900;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  color: #ffffff;
  background: ${({ $error }) => ($error ? 'rgba(70, 14, 14, 0.72)' : 'rgba(0, 0, 0, 0.24)')};
  font-size: 15px;
  text-align: center;
  /* 장면 교체 중 기존 드롭다운·파노라마를 조작하면 이전 scene 요청이 끼어들 수 있다. */
  pointer-events: ${({ $error }) => ($error ? 'auto' : 'none')};
  cursor: ${({ $error }) => ($error ? 'default' : 'wait')};
`;

const ViewerStatusCard = styled.div`
  display: grid;
  justify-items: center;
  gap: 12px;
  max-width: 360px;
  line-height: 1.45;
`;

const ViewerRetryButton = styled.button`
  padding: 8px 14px;
  color: #ffffff;
  background: #293683;
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 4px;
  font: inherit;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: #f04f25;
  }
`;

const ViewToolPanel = styled.aside`
  position: fixed;
  /* 미니맵(우측 하단 310px)과 겹치지 않도록 데스크톱에서는 왼쪽에 나란히 둔다. */
  right: 340px;
  bottom: 12px;
  width: 300px;
  max-height: calc(100vh - 24px);
  overflow-y: auto;
  padding: 12px 14px;
  /* 개발용 핀 배치 도구는 미니맵보다 항상 위에서 조작할 수 있어야 한다. */
  z-index: 2400;
  color: #f7f9fc;
  background: rgba(12, 22, 38, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 8px;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.28);
  font-size: 12px;

  @media (max-width: 700px) {
    /* 모바일에서는 미니맵(260×190) 위에 패널을 쌓되 미니맵을 가리지 않는다. */
    right: 10px;
    bottom: 210px;
    left: 10px;
    width: auto;
    max-height: calc(100vh - 230px);
  }
`;

const ViewToolTitle = styled.div`
  margin-bottom: 4px;
  font-weight: 700;
  font-size: 13px;
`;

const ViewToolScene = styled.div`
  margin-bottom: 10px;
  color: #cbd5e1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ViewToolValues = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4px 12px;
  margin-bottom: 10px;

  span {
    color: #aebbc9;
  }

  strong {
    font-variant-numeric: tabular-nums;
    color: #ffffff;
  }
`;

const ViewToolHint = styled.p`
  margin: 0 0 10px;
  color: #cbd5e1;
  line-height: 1.45;
`;

const ViewToolButton = styled.button`
  width: 100%;
  padding: 7px 10px;
  border: 0;
  border-radius: 4px;
  background: ${({ $theme }) => $theme.main};
  color: ${({ $theme }) => $theme.onMain};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.55 : 1)};

  &:hover:not(:disabled) {
    background: ${({ $theme }) => $theme.accent};
  }

  & + & {
    margin-top: 6px;
  }
`;

const ViewToolStatus = styled.div`
  margin-top: 8px;
  color: #f8c56d;
  line-height: 1.35;
`;

const ToolDivider = styled.hr`
  margin: 14px 0 12px;
  border: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
`;

const AdvancedToolDetails = styled.details`
  summary {
    color: #cbd5e1;
    cursor: pointer;
    user-select: none;
  }

  &[open] summary {
    margin-bottom: 10px;
    color: #ffffff;
  }
`;

const PinSelect = styled.select`
  width: 100%;
  margin: 0 0 8px;
  padding: 7px 8px;
  color: #0f1d2b;
  background: #ffffff;
  border: 0;
  border-radius: 4px;
  font: inherit;

  /* 대지역 안에 이미 표시된 핀도 선택은 가능하다. 굵은 취소선은 완료 여부만 구분한다. */
  option.pin-option-placed {
    color: #64748b;
    text-decoration: line-through;
    text-decoration-thickness: 3px;
    font-weight: 700;
  }

  &:disabled {
    opacity: 0.65;
  }
`;

const CalloutDirectionControl = styled.fieldset`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 8px;
  padding: 0;
  color: #cbd5e1;
  border: 0;

  legend {
    float: left;
    margin-right: 8px;
    padding: 0;
  }

  label {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    white-space: nowrap;
  }

  input {
    margin: 0 4px 0 0;
    accent-color: #f04f25;
  }

  &:disabled {
    opacity: 0.55;
  }
`;

const PinPlacementInfo = styled.div`
  margin: -1px 0 8px;
  color: #cbd5e1;
  font-variant-numeric: tabular-nums;
  line-height: 1.35;
`;

const DangerToolButton = styled.button`
  width: 100%;
  margin-top: 6px;
  padding: 7px 10px;
  color: #ffe8e8;
  background: rgba(157, 35, 35, 0.72);
  border: 1px solid rgba(255, 219, 219, 0.35);
  border-radius: 4px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.55 : 1)};
`;


const PopupOverlay = styled.div`
  position: fixed;
  z-index: 2000;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.4);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PopupBox = styled.div`
  width: min(430px, calc(100vw - 40px));
  padding: 28px 30px 22px;
  color: #17213a;
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(0,0,0,0.34);
`;

const PopupEyebrow = styled.div`
  margin-bottom: 6px;
  color: #f04f25;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-align: center;
`;

const PopupHeading = styled.h2`
  margin: 0;
  color: #293683;
  font-size: 22px;
  line-height: 1.35;
  text-align: center;
`;

const PopupText = styled.p`
  margin: 8px 0 18px;
  color: #526071;
  font-size: 14px;
  line-height: 1.55;
  text-align: center;
`;

const PopupGuideList = styled.ol`
  display: grid;
  gap: 10px;
  margin: 0 0 20px;
  padding: 15px 0;
  list-style: none;
  border-top: 1px solid #e6eaf0;
  border-bottom: 1px solid #e6eaf0;

  li {
    display: flex;
    align-items: center;
    gap: 9px;
    color: #334155;
    font-size: 13px;
    line-height: 1.35;
  }
`;

const GuideNumber = styled.span`
  display: inline-flex;
  width: 21px;
  height: 21px;
  flex: 0 0 21px;
  align-items: center;
  justify-content: center;
  color: ${({ $theme }) => $theme.onMain};
  background: ${({ $theme }) => $theme.main};
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
`;

const PopupOptions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  label {
    color: #526071;
    font-size: 13px;
  }
`;

const CloseButton = styled.button`
  padding: 6px 12px;
  font-size: 14px;
  background-color: ${({ $theme }) => $theme.main};
  color: ${({ $theme }) => $theme.onMain};
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: ${({ $theme }) => $theme.accent};
  }
`;
